import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Droplets, Filter, Box } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const WaterIntakeLandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Water Box & Specialty Filters | Intake Filtration | CTC Waterworks"
        description="Water Box and Basket with Bag Filters for intake and specialty filtration. Protect pumps and pressure filters with coarse screening and fine polishing options."
        canonical="https://www.ctcwaterworks.com/filtration/water-intake"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Water Box & Specialty</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <Droplets className="text-ctc-blue mr-4" size={56} />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Water Box & Specialty Filters
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our Water Box and Basket with Bag Filters serve as intake and specialty filtration solutions for irrigation and industrial applications. They protect pumps and pressure filters by removing larger solids at the source and offer fine polishing options when needed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Water Box Card */}
            <Link
              to="/filtration/water-intake/water-box"
              className="group bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-xl p-8 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <Box className="text-ctc-blue mr-4" size={48} />
                <h2 className="text-3xl font-bold text-gray-900">Water Box (WB)</h2>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                General-purpose intake screen system for ponds, canals, and sumps. Features large screen area and float-valved level control to deliver consistent flow to pumps and pressure filters.
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-ctc-blue rounded-full mr-3"></div>
                  <span>Flow range: 200-3000 GPM</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-ctc-blue rounded-full mr-3"></div>
                  <span>Float valve assembly for level control</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-ctc-blue rounded-full mr-3"></div>
                  <span>Large screen surface, low intake velocity</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-ctc-blue rounded-full mr-3"></div>
                  <span>Pairs with BIG or TIG filters downstream</span>
                </div>
              </div>

              <div className="flex items-center text-ctc-blue font-semibold group-hover:translate-x-2 transition-transform">
                View Water Box Specifications
                <ArrowRight className="ml-2" size={20} />
              </div>
            </Link>

            {/* BWB Card */}
            <Link
              to="/filtration/water-intake/basket-with-bag"
              className="group bg-gradient-to-br from-orange-50 to-white border-2 border-orange-300 rounded-xl p-8 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <Filter className="text-orange-600 mr-4" size={48} />
                <h2 className="text-3xl font-bold text-gray-900">Basket with Bag (BWB)</h2>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Specialty filtration unit combining rigid metal basket and replaceable bag insert. Provides fine filtration for algae, silt, and micro-particulate control in irrigation and process water.
              </p>

              <div className="space-y-2 mb-6">
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                  <span>Filter bags: 1-200 micron range</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                  <span>Removable basket for easy maintenance</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                  <span>Flanged lid with swing-bolt clamps</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                  <span>Fine polishing for targeted micron capture</span>
                </div>
              </div>

              <div className="flex items-center text-orange-600 font-semibold group-hover:translate-x-2 transition-transform">
                View BWB Specifications
                <ArrowRight className="ml-2" size={20} />
              </div>
            </Link>
          </div>
        </div>
      </section>


      {/* Cross-Links */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Complete Your Filtration System</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Add downstream BIG inline filters for secondary filtration. Water Box or Basket with Bag handles intake, BIG provides pressure filtration and automatic cleaning.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View BIG Series
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/tig"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with TIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compact true-inline TIG filters for space-constrained installations. Works downstream of Water Box or Basket with Bag for final polishing.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                View TIG Series
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaterIntakeLandingPage;
